import { lazyGettext } from '@eb/i18n';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { constants as destinationConstants } from '@eb/destination-utils';

import { SEASONAL_TAB, TABS_CONFIG } from '../constants/tabConfig';
import { PRIMARY_BUCKET, SECONDARY_BUCKET } from '../constants/constants';
import { SEASONAL_CONTENT } from '../constants/seasonal';

const { CURRENT_FUTURE } = destinationConstants;

/**
 * An object containing seasonal content like images, texts and links to curated pages.
 * @typedef {Object} SeasonalContent
 * @property {Object} byDate - if there is any seasonal content to show by date, all the info will be here.
 * @property {Object} byPlace - if there is any seasonal content to show by place, well, you know.
 *
 * An object containing seasonal content like images, texts and links to curated pages.
 * @typedef {Object} HeaderContent
 * @property {Boolean} shouldShowCustomContent - boolean that will be used to show or not the custom content.
 * @property {Object} images - an object of images with the different breakpoints by key.
 * @property {String} attribution - the image attribution to show near the image.
 * @property {String} customContentCtaLabel - if existent, shows a custom message to be shown in the header.
 * @property {String} customCombinedHeaderTitleAndCta - if existent, combines the title and CTA into one.
 * @property {String} customContentCtaLink - if existent, the link to a custom collection of events or other similar page.
 * @property {String} customContentCtaPrefix - if existent, a prefix for the content to be added in analytics.
 * @property {String} customLinkLabel - if existent, the label of the link.
 */

/**
 * Returns a string that represents the parameter date in the format yyyy-mm-dd
 * @param  {{Date}} date
 * @return {string}
 */
const getSQLFormattedDate = (date) => {
    let formattedDate = '';

    if (date instanceof Date && !isNaN(date.valueOf())) {
        const yyyy = date.getFullYear().toString();
        let mm = date.getMonth() + 1;
        let dd = date.getDate();

        /*
         * If the date is lower than 10, add a 0 to get the proper format
         * i.e. 9 returns 09
         */
        if (dd < 10) {
            dd = `0${dd}`;
        }

        /*
         * If the month is lower than 10, add a 0 to get the proper format
         * i.e. 7 returns 07
         */
        if (mm < 10) {
            mm = `0${mm}`;
        }

        /*
         * i.e. 2019-07-09
         */
        formattedDate = `${yyyy}-${mm}-${dd}`;
    }
    return formattedDate;
};

/**
 * Returns the current seasonal content available according to the start and end dates.
 * Each seasonal content has a way to disable that campaing by force
 * @param  {{integer}} placeId
 *         A number representing the place you are currently browsing.
 * @return {SeasonalContent}
 */
export const getSeasonalContent = ({ placeId } = {}) => {
    const today = new Date();
    const seasonalContent = {
        byDate: {},
        byPlace: {},
    };

    Object.keys(SEASONAL_CONTENT.default).forEach((season) => {
        const _seasonalContent = SEASONAL_CONTENT.default[season];

        const startMonth = _seasonalContent.startDate.month;
        const endMonth = _seasonalContent.endDate.month;

        const todayYear = today.getFullYear();

        //If startMonth month < end month > year changed, use today.getFullYear + 1
        const _startDateYear = _seasonalContent.startDate.year || todayYear;
        const _endDateYear = _seasonalContent.endDate.year || todayYear;

        const _startDate = new Date(
            _startDateYear,
            startMonth - 1,
            _seasonalContent.startDate.day,
        );
        const _endDate = new Date(
            _endDateYear,
            endMonth - 1,
            _seasonalContent.endDate.day,
        );

        const isAfterContentDate = today >= _startDate;
        const isBeforeContentDate = today <= _endDate;

        if (
            isAfterContentDate &&
            isBeforeContentDate &&
            !_seasonalContent.forceDisable
        ) {
            _seasonalContent.currentStartDate = today;
            _seasonalContent.currentEndDate = _endDate;
            seasonalContent.byDate = _seasonalContent;
        }
    });

    const seasonalContentKey = get(seasonalContent, 'byDate.key');

    const locationSpecificContent = get(
        SEASONAL_CONTENT,
        `${placeId}.${seasonalContentKey}`,
    );

    if (locationSpecificContent) {
        seasonalContent.byPlace = locationSpecificContent;
    } else {
        const defaultSeasonalContent = get(
            SEASONAL_CONTENT,
            `default.${seasonalContentKey}`,
        );

        if (defaultSeasonalContent && defaultSeasonalContent.alwaysShow) {
            seasonalContent.byPlace = defaultSeasonalContent;
        }
    }

    return seasonalContent;
};

/**
 * Returns the tabs config for the page but inserting the seasonal content if it exists.
 * @param  {SeasonalContent} seasonalContent
 * @return {Object} tabsConfig
 *         Tabs config for the page.
 */
export const inyectSeasonalTab = ({
    seasonalContent: {
        byDate: {
            key,
            tabName,
            eventSearchQ,
            onlineOnly = false,
            currentStartDate,
            currentEndDate,
        } = {},
    } = {},
} = {}) => {
    const tabsConfig = TABS_CONFIG;

    let dateRange = '';
    const dateRangeStart = getSQLFormattedDate(currentStartDate);
    const dateRangeEnd = getSQLFormattedDate(currentEndDate);

    if (!isEmpty(dateRangeStart) && !isEmpty(dateRangeEnd)) {
        dateRange = {
            from: dateRangeStart,
            to: dateRangeEnd,
        };
    }

    if (key) {
        tabsConfig[SEASONAL_TAB] = {
            tabKeyLabel: key,
            name: tabName,
            eventSearch: {
                q: eventSearchQ || tabName,
                dates: [CURRENT_FUTURE],
                date_range: dateRange,
                online_events_only: onlineOnly,
            },
            follow: [],
            feedConfig: {
                [PRIMARY_BUCKET]: {
                    order: 1,
                    propsOverwrite: {
                        name: '',
                    },
                    emptyState: [
                        {
                            validation: {
                                shouldShowEmptyState: true,
                                isAuthenticated: true,
                                useDefault: true,
                            },
                        },
                        {
                            validation: {
                                shouldShowEmptyState: true,
                                isAuthenticated: false,
                                useDefault: true,
                            },
                        },
                    ],
                },
                [SECONDARY_BUCKET]: {
                    order: 2,
                    propsOverwrite: {
                        name: lazyGettext('More events for %(season)s', {
                            season: tabName,
                        }),
                    },
                    emptyState: [
                        {
                            validation: {
                                shouldShowEmptyState: false,
                                isAuthenticated: true,
                            },
                        },
                        {
                            validation: {
                                shouldShowEmptyState: false,
                                isAuthenticated: false,
                            },
                        },
                    ],
                },
            },
        };
    }

    return tabsConfig;
};

/**
 * Returns the tabs config for the page but inserting the seasonal content if it exists.
 * @param  {SeasonalContent} seasonalContent
 * @return {HeaderContent} seasonalContent
 */
export const getSeasonalHeaderContent = ({ byDate, byPlace } = {}) => {
    const shouldShowCustomHeader =
        byDate &&
        !byDate.forceDisable &&
        byPlace &&
        byPlace.shouldShowCustomHeader;

    let seasonalContent = {};

    if (shouldShowCustomHeader) {
        seasonalContent = {
            shouldShowCustomContent: byPlace.shouldShowCustomHeader,
            images: byDate.headerImages,
            attribution: byDate.attribution,
            customContentCtaLabel: byDate.cta,
            customContentTitle: byDate.headerTitle,
            customCombinedHeaderTitleAndCta: byDate.combinedHeaderTitleAndCta,
            customContentCtaLink: byPlace.collectionLink,
            customContentCtaPrefix: byPlace.collectionPrefix,
            customLinkLabel: byDate.linkLabel,
        };
    }

    return seasonalContent;
};
